import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Navbar from "../components/Navbar";
import NavbarMob from "../components/NavbarMob";
import Navbar2 from "../components/Navbar2";
import Navbar2Mob from "../components/Navbar2Mob";
import Footer from "../components/Footer";
import { useMediaQuery } from "react-responsive";

const StaffCodeJobs = () => {
    const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "";
    const customerType = sessionStorage.getItem("customerType");
    const [data, setData] = useState({});
    const [selectedStaffCode, setSelectedStaffCode] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const res = await fetch(`${apiBaseUrl}/staff-code-jobposts`);
                const result = await res.json();
                if (!res.ok || !result.success) throw new Error(result.message || "Failed to load staff code data");
                setData(result.data || {});
            } catch (err) {
                setError(err.message || "Failed to load staff code data");
            } finally {
                setLoading(false);
            }
        };

        if (apiBaseUrl) fetchData();
    }, [apiBaseUrl]);

    const staffCodes = useMemo(() => Object.keys(data).sort(), [data]);
    const visibleCodes = selectedStaffCode ? [selectedStaffCode] : staffCodes;
    const getEntryDateTime = (entry) => {
        if (!entry.createdAt) return null;
        const date = new Date(entry.createdAt);
        return Number.isNaN(date.getTime()) ? null : date;
    };
    const isEntryInDateRange = (entry) => {
        const entryDate = getEntryDateTime(entry);
        if (!entryDate) return !fromDate && !toDate;

        if (fromDate) {
            const from = new Date(`${fromDate}T00:00:00`);
            if (entryDate < from) return false;
        }

        if (toDate) {
            const to = new Date(`${toDate}T23:59:59.999`);
            if (entryDate > to) return false;
        }

        return true;
    };
    const getFilteredEntries = (staffCode) => (data[staffCode] || []).filter(isEntryInDateRange);
    const hasActiveDateFilter = Boolean(fromDate || toDate);

    if (customerType !== "mainAdmin") {
        return (
            <div className="min-h-screen flex flex-col bg-gray-50">
                {isMobile ? <NavbarMob /> : <Navbar />}
                <div className="hidden md:block">
                    <Navbar2 />
                </div>
                <div className="md:hidden">
                    <Navbar2Mob />
                </div>
                <div className="flex-1 flex items-center justify-center px-4 text-center">
                    <p className="text-lg font-semibold text-red-600">Access denied.</p>
                </div>
                <Footer />
            </div>
        );
    }

    const exportToExcel = () => {
        const rows = visibleCodes.flatMap((staffCode) =>
            getFilteredEntries(staffCode).map((entry) => ({
                "Staff Code": staffCode,
                "Sl No": entry.slNo,
                "Saved Date": entry.createdAt ? new Date(entry.createdAt).toLocaleString("en-IN") : "",
                "Job ID": entry.manualJobID || entry.savedJobId || "",
                "Job Title": entry.jobTitle || "",
                Job: entry.job || "",
                "Shop Name": entry.shopName || "",
                Gender: entry.genderType || "",
                Qualification: entry.qualification || "",
                "Minimum Salary": entry.minSalary || "",
                "Maximum Salary": entry.maxSalary || "",
                "Salary Type": entry.salaryType || "",
                Experience: entry.experienceType || "",
                Category: entry.companyType || "",
                Location: entry.location || "",
                Description: entry.jobDescription || "",
                "Manager Number": entry.whatsappNumber || "",
                "Office Number": entry.officeNumber || "",
                District: entry.district || "",
                Address: entry.address || "",
                Area: entry.area || "",
            })),
        );

        if (rows.length === 0) {
            alert("No staff code data to export");
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Staff Code Jobs");
        const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([buffer], { type: "application/octet-stream" });
        const suffix = selectedStaffCode || "all";
        saveAs(blob, `staff-code-jobs-${suffix}.xlsx`);
    };

    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            {isMobile ? <NavbarMob /> : <Navbar />}
            <div className="hidden md:block">
                <Navbar2 />
            </div>
            <div className="md:hidden">
                <Navbar2Mob />
            </div>

            <div className="flex-1 px-4 py-8 sm:px-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                    <h1 className="text-2xl font-bold text-gray-800">Staff Code Job Data</h1>
                    <div className="flex flex-col sm:flex-row gap-3">
                        {hasActiveDateFilter && (
                            <button
                                onClick={() => {
                                    setFromDate("");
                                    setToDate("");
                                }}
                                className="h-[42px] px-4 rounded-md border border-gray-300 bg-white text-gray-700 font-semibold hover:bg-gray-100"
                            >
                                Clear
                            </button>
                        )}
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="h-[42px] border border-gray-300 rounded-md px-3 bg-white"
                            aria-label="From date"
                        />
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            min={fromDate || undefined}
                            className="h-[42px] border border-gray-300 rounded-md px-3 bg-white"
                            aria-label="To date"
                        />
                        <select
                            value={selectedStaffCode}
                            onChange={(e) => setSelectedStaffCode(e.target.value)}
                            className="h-[42px] border border-gray-300 rounded-md px-3 bg-white"
                        >
                            <option value="">All Staff Codes</option>
                            {staffCodes.map((staffCode) => (
                                <option key={staffCode} value={staffCode}>
                                    {staffCode}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={exportToExcel}
                            className="h-[42px] px-5 rounded-md bg-green-600 text-white font-semibold hover:bg-green-700"
                        >
                            Export Excel
                        </button>
                    </div>
                </div>

                {loading && <p className="text-center text-gray-600 py-12">Loading...</p>}
                {error && <p className="text-center text-red-600 py-12">{error}</p>}

                {!loading && !error && (
                    <div className="space-y-8">
                        {visibleCodes.length === 0 ? (
                            <p className="text-center text-gray-600 py-12">No staff code entries found.</p>
                        ) : (
                            visibleCodes.map((staffCode) => {
                                const entries = getFilteredEntries(staffCode);

                                return (
                                    <div key={staffCode} className="bg-white rounded-lg shadow overflow-hidden">
                                        <div className="bg-blue-700 text-white px-4 py-3 font-bold">
                                            Staff Code: {staffCode}
                                        </div>
                                        <div className="overflow-x-auto">
                                            {entries.length === 0 ? (
                                                <p className="text-center text-gray-600 py-8">
                                                    No entries for selected date range.
                                                </p>
                                            ) : (
                                                <table className="min-w-full border-collapse">
                                                    <thead className="bg-gray-100">
                                                        <tr>
                                                            {[
                                                                "Sl No",
                                                                "Job ID",
                                                                "Job Title",
                                                                "Job",
                                                                "Shop",
                                                                "Location",
                                                                "Manager",
                                                                "Address",
                                                                "Saved Date",
                                                            ].map((header) => (
                                                                <th key={header} className="border px-3 py-2 text-left">
                                                                    {header}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {entries.map((entry) => (
                                                            <tr key={`${staffCode}-${entry.slNo}`}>
                                                                <td className="border px-3 py-2">{entry.slNo}</td>
                                                                <td className="border px-3 py-2">
                                                                    {entry.manualJobID || entry.savedJobId || ""}
                                                                </td>
                                                                <td className="border px-3 py-2">{entry.jobTitle}</td>
                                                                <td className="border px-3 py-2">{entry.job}</td>
                                                                <td className="border px-3 py-2">{entry.shopName}</td>
                                                                <td className="border px-3 py-2">{entry.location}</td>
                                                                <td className="border px-3 py-2">{entry.whatsappNumber}</td>
                                                                <td className="border px-3 py-2">{entry.address}</td>
                                                                <td className="border px-3 py-2">
                                                                    {entry.createdAt
                                                                        ? new Date(entry.createdAt).toLocaleString("en-IN")
                                                                        : ""}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
            </div>

            <Footer />
        </div>
    );
};

export default StaffCodeJobs;
